import { Breadcrumb } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import {
  DEFAULT_PAGE_BANNER,
  pageBannerImage,
  pageBannerUrl,
  usePageBanner,
} from "./usePageBanner";
import PageBannerImage from "./PageBannerImage";

interface Props {
  banner: string;
  current: string;
  link: string;
}
const PageHeader = ({ banner, current, link }: Props) => {
  const { t, i18n } = useTranslation();
  const { banner: apiBanner, loading } = usePageBanner();
  const fallbackImage = banner?.trim() || DEFAULT_PAGE_BANNER;
  const image = loading ? null : pageBannerImage(apiBanner?.image_1920) || fallbackImage;
  const heading = apiBanner?.first_title || "";
  const subtitle = apiBanner?.second_title || "";
  const target = pageBannerUrl(apiBanner?.url);
  const bannerWrapRef = useRef<HTMLDivElement>(null);
  const [bannerInView, setBannerInView] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setBannerInView(true);
      return;
    }
    const el = bannerWrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBannerInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.06, rootMargin: "0px 0px -4% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="page-header pt-30 background-body">
      <div className="custom-container position-relative mx-auto">
        <div
          ref={bannerWrapRef}
          // ponytail: no position-relative here — the stylesheet makes
          // .custom-container .bg-overlay absolute, and adding relative
          // collapses the wrapper to zero height (blank white banner).
          className={`bg-overlay rounded-20 overflow-hidden ac-page-header-banner${
            bannerInView ? " ac-page-header-banner--visible" : ""
          }`}
        >
          <PageBannerImage
            key={image ?? "loading"}
            className="w-100 h-100 img-banner"
            src={image}
            fallback={fallbackImage}
          />
          {target && <a href={target} className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 2 }} aria-label={heading || current} />}
        </div>
        <div className="container position-absolute z-1  start-50 translate-middle banner-title">
          {heading && <h4 className="ac--font-expo text-white">{heading}</h4>}
          {subtitle && <h4 className="ac--font-expo text-white ">{subtitle}</h4>}
          </div>
        <div className="ac--breadcrumbs background-body position-absolute z-1 top-100 start-50 translate-middle px-3 py-2 rounded-12 border d-flex gap-3 ac">
          <Breadcrumb
            separator={
              i18n.language === "ar" ? (
                <IoIosArrowBack />
              ) : (
                <IoIosArrowForward />
              )
            }
            items={[
              {
                title: (
                  <span className="ac--color-extra-light-gray text-md-medium">
                    {t("menu.home")}
                  </span>
                ),
                href: "/",
              },
              {
                title: (
                  <span className="ac--color-primary text-md-bold">
                    {current}
                  </span>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};
export default PageHeader;
